import React, { useState, useEffect, useRef, useCallback } from 'react';
import { MapPin, Plus, Search } from 'lucide-react';

interface Location {
  id: number;
  adresa: string;
  oras?: string;
  judet?: string;
  tara: string;
  tip: 'plecare' | 'destinatie';
}

interface AddressDropdownProps {
  value?: Location | null;
  onChange: (location: Location | null) => void;
  city: string; // orașul selectat din CitySelector
  type: 'plecare' | 'destinatie';
  placeholder?: string;
  required?: boolean;
}

const API_BASE_URL = `${import.meta.env.VITE_BACKEND_URL}/api`;

const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
};

const AddressDropdown: React.FC<AddressDropdownProps> = ({
  value,
  onChange,
  city,
  type,
  placeholder = "Caută sau adaugă o adresă",
  required = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState(value?.adresa || '');
  const [locations, setLocations] = useState<Location[]>([]);
  const [filteredLocations, setFilteredLocations] = useState<Location[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showCreateOption, setShowCreateOption] = useState(false);
  
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);



  const fetchLocations = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(
        `${API_BASE_URL}/locations?city=${encodeURIComponent(city)}&type=${type}`,
        {
          headers: getAuthHeaders(),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setLocations(data.locations || []);
    } catch (err) {
      console.error('Error fetching locations:', err);
      setError('Eroare la încărcarea locațiilor');
      setLocations([]);
    } finally {
      setLoading(false);
    }
  },[city, type]);

  
  // Fetch locations when component mounts or city changes
  useEffect(() => {
    if (city) {
      fetchLocations();
    }
  }, [city, fetchLocations,type]);

  // Filter locations based on search term
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredLocations(locations);
      setShowCreateOption(false);
      return;
    }

    const filtered = locations.filter(location =>
      location.adresa.toLowerCase().includes(searchTerm.toLowerCase())
    );
    
    setFilteredLocations(filtered);
    
    // Show create option if no exact match found and search term has content
    const exactMatch = locations.some(
      location => location.adresa.toLowerCase() === searchTerm.toLowerCase()
    );
    setShowCreateOption(searchTerm.trim().length > 0 && !exactMatch);
  }, [searchTerm, locations]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  

  const createNewLocation = async (adresa: string): Promise<Location | null> => {
    try {
      const response = await fetch(`${API_BASE_URL}/locations`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          adresa: adresa.trim(),
          oras: city,
          tip: type,
          tara: 'Romania'
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.location;
    } catch (err) {
      console.error('Error creating location:', err);
      setError('Eroare la crearea locației');
      return null;
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setSearchTerm(newValue);
    
    if (!isOpen) {
      setIsOpen(true);
    }

    // Clear selection if input doesn't match current value
    if (value && newValue !== value.adresa) {
      onChange(null);
    }
  };

  const handleInputClick = () => {
    setIsOpen(true);
    if (!locations.length && city) {
      fetchLocations();
    }
  };

  const handleLocationSelect = (location: Location) => {
    setSearchTerm(location.adresa);
    onChange(location);
    setIsOpen(false);
  };

  const handleCreateNew = async () => {
    if (!searchTerm.trim() || !city) return;

    setLoading(true);
    const newLocation = await createNewLocation(searchTerm.trim());
    
    if (newLocation) {
      // Add to local list
      setLocations(prev => [...prev, newLocation]);
      // Select the new location
      handleLocationSelect(newLocation);
    }
    
    setLoading(false);
  };

  const handleInputKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && showCreateOption && searchTerm.trim()) {
      e.preventDefault();
      handleCreateNew();
    } else if (e.key === 'Escape') {
      setIsOpen(false);
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={searchTerm}
          onChange={handleInputChange}
          onClick={handleInputClick}
          onKeyDown={handleInputKeyDown}
          className={`w-full px-4 py-3 pr-10 border rounded-lg focus:ring-2 focus:ring-orange-500 ${
            error ? 'border-red-300' : 'border-gray-300'
          }`}
          placeholder={city ? placeholder : "Selectează mai întâi un oraș"}
          required={required}
          disabled={!city}
        />
        
        <div className="absolute inset-y-0 right-0 flex items-center pr-3">
          {loading ? (
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-orange-500"></div>
          ) : (
            <Search className="h-4 w-4 text-gray-400" />
          )}
        </div>
      </div>

      {error && (
        <p className="mt-1 text-sm text-red-600">{error}</p>
      )}

      {/* Dropdown */}
      {isOpen && city && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
          {loading ? (
            <div className="px-4 py-3 text-sm text-gray-500 flex items-center">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-orange-500 mr-2"></div>
              Se încarcă...
            </div>
          ) : (
            <>
              {/* Existing locations */}
              {filteredLocations.length > 0 && (
                <>
                  {filteredLocations.map((location) => (
                    <button
                      key={location.id}
                      onClick={() => handleLocationSelect(location)}
                      className="w-full px-4 py-3 text-left hover:bg-gray-50 focus:bg-gray-50 focus:outline-none flex items-center"
                    >
                      <MapPin className="h-4 w-4 text-gray-400 mr-2 flex-shrink-0" />
                      <div className="flex-grow">
                        <div className="text-sm font-medium text-gray-900">
                          {location.adresa}
                        </div>
                        {location.oras && (
                          <div className="text-xs text-gray-500">
                            {location.oras}
                            {location.judet && `, ${location.judet}`}
                          </div>
                        )}
                      </div>
                    </button>
                  ))}
                  
                  {showCreateOption && <hr className="border-t border-gray-200" />}
                </>
              )}

              {/* Create new option */}
              {showCreateOption && (
                <button
                  onClick={handleCreateNew}
                  disabled={loading}
                  className="w-full px-4 py-3 text-left hover:bg-green-50 focus:bg-green-50 focus:outline-none flex items-center text-green-700 disabled:opacity-50"
                >
                  <Plus className="h-4 w-4 mr-2 flex-shrink-0" />
                  <div className="flex-grow">
                    <div className="text-sm font-medium">
                      Creează: "{searchTerm.trim()}"
                    </div>
                    <div className="text-xs text-green-600">
                      în {city}
                    </div>
                  </div>
                </button>
              )}

              {/* No results */}
              {filteredLocations.length === 0 && !showCreateOption && !loading && (
                <div className="px-4 py-3 text-sm text-gray-500 text-center">
                  {searchTerm ? 'Nu s-au găsit rezultate' : 'Începe să scrii pentru a căuta'}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default AddressDropdown;